<template>
	<div class="flow-breadcrumb-container">
		<template v-for="(crumb, index) in crumbs">
			<div
				:key="'crumb_' + crumb.label"
				v-tooltip="{
					content: crumb.label,
					placement: 'bottom',
					classes: ['black']
				}"
				class="flow-b-crumb"
				:class="{ 'crumb-avatar': !crumb.icon && !crumb.emoji }"
				:state="crumb.state ? crumb.state : 'default'"
				:style="crumbStyle"
				:tabindex="crumb.tabindex ? crumb.tabindex : 0"
				:textOnly="textOnly"
				:data-qa-crumb-id="crumb.qaId"
				@click="handleClick(crumb)"
			>
				<template v-if="!textOnly">
					<Icon
						v-if="crumb.icon"
						:name="crumb.icon.name"
						:type="crumb.icon.type ? crumb.icon.type : 'filled'"
						:state="crumb.icon.state ? crumb.icon.state : 'default'"
						size="20px"
						:effects="false"
					/>
					<template v-if="crumb.emoji">
						<span class="flow-b-crumb-emoji" v-html="crumb.emoji" />
					</template>
					<Avatar
						v-if="!crumb.icon && !crumb.emoji"
						:ref="'crumb-avatar-' + index"
						:label="crumb.shortLabel ? crumb.shortLabel : crumb.label"
						:size="20"
						shape="square"
					/>
				</template>
				<Typography type="p2" color="dark" overflow="ellipsis" class="flow-crumb-label">
					{{ crumb.label }}
				</Typography>
			</div>
		</template>
		<p
			v-if="!textOnly"
			v-tooltip="{
				content: lastcrumblabel,
				placement: 'bottom',
				classes: ['black']
			}"
			class="fc-normal paragraph-2 line-h-16 text-ellipsis last-crumb-label flow-crumb-label"
		>
			{{ lastcrumblabel }}
		</p>
	</div>
</template>
<script>
import { defineComponent } from "vue";
import Icon from "./../Icon/Icon";
import Avatar from "./../Avatar/Avatar";
import Typography from "./../Typography/Typography";
import { VTooltip } from "v-tooltip";

export default defineComponent({
	name: "Breadcrumbs",
	components: {
		Icon,
		Avatar,
		Typography
	},
	directives: {
		tooltip: VTooltip
	},
	props: {
		/**
		 * <code>
		 * <pre>
		 * [
		 *  {
		 * 		// Note : Please refer Icon component props while passing icon object
		 * 		icon:{
		 * 			name : String,
		 * 			type: String, 'filled',
		 * 			state : String, 'default',
		 * 		},
		 * 		emoji: String
		 * 		// 'default' | 'primary' | 'success' | 'success-light' | 'success-dark' | 'error' | 'error-light' | 'error-dark' | 'warning' | 'light' | 'dark'
		 * 		state : String, 'default',
		 * 		label : String,
		 * 		shortLabel : String,
		 * 		tabindex : Number, -1
		 *  }, ...
		 * ]
		 * </pre>
		 * </code>
		 */
		crumbs: {
			type: Array,
			default: () => []
		},
		max: {
			type: Number,
			default: null
		},
		/**
		 * Max crumb width
		 */
		crumbWidth: {
			type: Number,
			default: 150
		},
		/**
		 * Is text only breadcrumb?
		 */
		textOnly: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		lastcrumblabel() {
			if (this.crumbs && this.crumbs.length > 0) {
				return this.crumbs[this.crumbs.length - 1].label;
			}
			return "";
		},
		crumbStyle() {
			if (this.textOnly) {
				return { maxWidth: this.crumbWidth ? `${this.crumbWidth}px` : "150px" };
			}
			return { maxWidth: this.crumbWidth ? `${this.crumbWidth}px` : "150px" };
		}
	},
	methods: {
		handleClick(crumb) {
			/**
			 * Click event.
			 *
			 * @event click
			 */
			this.$emit("click", crumb);
		}
	}
});
</script>
<style lang="scss">
$crumb-states: (
	"default": var(--dashboard-element-light),
	"primary": var(--dashboard-primary),
	"success": var(--dashboard-success),
	"success-light": var(--dashboard-success-light),
	"success-dark": var(--dashboard-success-dark),
	"error": var(--dashboard-danger),
	"error-light": var(--dashboard-danger-light),
	"error-dark": var(--dashboard-danger-dark),
	"warning": var(--dashboard-warning),
	"light": var(--dashboard-element-light),
	"dark": var(--dashboard-element-dark),
	"border": var(--dashboard-border)
);
div.flow-breadcrumb-container {
	display: flex;
	max-height: 36px;
	align-items: center;

	div.flow-b-crumb + div.flow-b-crumb {
		margin-left: 4px;
		&[textOnly] {
			margin-left: 12px;
		}
	}
	div.flow-b-crumb {
		height: 100%;
		cursor: pointer;
		border-radius: 4px;
		background-color: var(--dashboard-element);

		display: inline-flex;
		align-items: center;
		padding: 8px;

		&:not([textOnly]) {
			&:first-of-type,
			&:last-of-type,
			&:only-of-type {
				width: 36px !important;
			}
		}

		&[textOnly] {
			background-color: transparent !important;
			position: relative;
			outline: none;
			height: 24px;
			&:first-child {
				padding: 8px 8px 8px 0px;
			}
			&:after {
				content: "/";
				position: absolute;
				left: 100%;
				top: 2px;
				font-size: 12px;
				color: var(--dashboard-font-dark);
				width: 12px;
				display: flex;
				justify-content: center;
			}

			&:last-child {
				&:after {
					content: none;
				}
			}

			&:hover {
				* {
					color: var(--dashboard-primary);
				}
			}
		}

		> * + * {
			margin-left: 8px;
		}

		> .flow-b-crumb-emoji {
			font-size: 20px;
			line-height: 20px;
		}
		&:not([textOnly]) {
			&:last-of-type,
			&:only-of-type {
				position: relative;
				border-top-right-radius: 0px;
				border-bottom-right-radius: 0px;
				&:after {
					position: absolute;
					content: "";
					top: 0px;
					bottom: 0px;
					left: 100%;

					border-right: 18px solid transparent;
					border-top: 18px solid transparent;
					border-bottom: 18px solid transparent;
				}

				&:focus {
					box-shadow: none;
					outline: none;
				}
			}
		}
		@each $state, $color in $crumb-states {
			&[state="#{$state}"] {
				background-color: $color;

				&:not([textOnly]) {
					&:last-of-type,
					&:only-of-type {
						&:after {
							border-left: 10px solid $color;
						}
					}
				}
			}
		}
		&:not([textOnly]) {
			&:active {
				filter: contrast(80%);
			}

			&:hover:not(:active) {
				filter: contrast(85%);
			}
		}
	}

	> .last-crumb-label {
		max-width: 250px;
		margin-left: 18px;
	}
}
</style>
